import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import ParticleField from './ParticleField.jsx';
import { CONTACT } from '../content/site.js';

/**
 * The lockup, always as the supplied file.
 *
 * The brand guidelines are explicit that the wordmark is never redrawn or
 * retyped, so it is an <img> rather than styled text — which also means the
 * header can never drift out of step with the deck or the letterhead. The
 * on-dark file carries white type; the mark stays red in both.
 */
function Lockup({ height = 42 }) {
  return (
    <img
      src="/brand/lockup-ondark.png"
      alt="Ennoveda — Brilliance at the Basics"
      width={Math.round(height * 4.691)}
      height={height}
      style={{ height, width: 'auto' }}
    />
  );
}

const NAV = [
  ['/services', 'Services'],
  ['/blog', 'Cyber Brief'],
  ['/checker', 'Domain Check'],
  ['/about', 'About'],
  ['/trust', 'Trust'],
  ['/contact', 'Contact'],
];

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Route changes must move the viewport back to the top, and must move focus
  // too — a screen reader left where the last page ended has no idea anything
  // happened.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <ParticleField />
      <a className="skip" href="#main">Skip to content</a>

      <header className={`site-header${scrolled ? ' scrolled' : ''}`}>
        <div className="shell header-inner">
          <Link to="/" className="lockup-link" aria-label="Ennoveda — home">
            <Lockup height={40} />
          </Link>

          <nav className="nav-desktop" aria-label="Main">
            {NAV.map(([to, label]) => (
              <NavLink key={to} to={to} className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                {label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            className="nav-toggle"
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
            <span aria-hidden="true" className={`burger${open ? ' x' : ''}`}><i /><i /><i /></span>
          </button>
        </div>

        <nav id="mobile-nav" className={`nav-mobile${open ? ' open' : ''}`} aria-label="Main">
          {NAV.map(([to, label]) => (
            <NavLink key={to} to={to} className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main id="main">{children}</main>

      <footer className="site-footer">
        <div className="shell">
          <div className="footer-grid">
            <div>
              <Lockup height={38} />
              <p className="dim" style={{ marginTop: '1rem', maxWidth: '26rem', fontSize: '0.92rem' }}>
                IT and cybersecurity consulting. From the chip to the cloud — securing operations,
                meeting regulation, and keeping the fundamentals right.
              </p>
            </div>

            <div>
              <h3 className="footer-head">Company</h3>
              <ul className="footer-list">
                <li><Link to="/services">Services</Link></li>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/trust">Trust Center</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="footer-head">Resources</h3>
              <ul className="footer-list">
                <li><Link to="/blog">Cyber Brief</Link></li>
                <li><Link to="/checker">Domain Check</Link></li>
                <li><a href="https://staysafe.ennoveda.net">Free Safety Training</a></li>
                <li><a href="https://vedajustice.com">VedaJustice</a></li>
                <li><a href="/feed.xml">RSS</a></li>
              </ul>
            </div>

            <div>
              <h3 className="footer-head">Contact</h3>
              <ul className="footer-list">
                <li><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></li>
                <li><a href={`tel:${CONTACT.phoneHref}`}>{CONTACT.phone}</a></li>
                <li className="faint" style={{ marginTop: '0.6rem', lineHeight: 1.5 }}>
                  {CONTACT.address.slice(1).map((l) => <span key={l}>{l}<br /></span>)}
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-base">
            <p className="faint" style={{ margin: 0, fontSize: '0.82rem' }}>
              © {new Date().getFullYear()} Ennoveda Inc. All rights reserved.
            </p>
            <p style={{ margin: 0, fontSize: '0.82rem' }}>
              <Link to="/privacy">Privacy</Link>
              <span className="faint" style={{ margin: '0 0.6rem' }}>·</span>
              <Link to="/terms">Terms</Link>
              <span className="faint" style={{ margin: '0 0.6rem' }}>·</span>
              <a href="/.well-known/security.txt">security.txt</a>
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
